/**
 * RunStatusStrip - the single live status line for the active run, pinned
 * directly above the chat composer (below the UserInteractionDrawer when a
 * question is open, so the order is: question card → status → input).
 *
 * Purely presentational: a pulsing dot + the current activity label + elapsed
 * time + model attribution. It opens nothing - the full step trace lives in
 * the conversation itself (RunStepChain inside ChatRunView).
 */

import { getExecutionSummary, isExecutionActive, isExecutionTerminal } from '@protoss/shared';
import * as React from 'react';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { formatDuration } from '../../../lib/format';
import type { AssistantRun } from '../state/chatState';

const ACTIVE_LIFECYCLES = new Set([
  'pending_start',
  'streaming',
  'resuming',
  'finalizing',
  'awaiting_confirmation',
]);

/** Ticks once a second while mounted; returns elapsed ms since `startedAt`. */
function useElapsedMs(startedAt: string): number {
  const startMs = React.useMemo(() => new Date(startedAt).getTime(), [startedAt]);
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return Math.max(0, now - startMs);
}

export interface RunStatusStripProps {
  /** The currently active run, or null when idle (renders nothing). */
  run: AssistantRun | null;
}

export function RunStatusStrip({ run }: RunStatusStripProps) {
  if (!run || !ACTIVE_LIFECYCLES.has(run.lifecycleState)) return null;
  // Defensive: once execution is terminal the strip must never show, even if
  // activeRunId clears a render late - otherwise a stale "Preparing"/step label
  // could flash next to the chain's "Done …" summary row.
  if (run.executionStatus && isExecutionTerminal(run.executionStatus.phase)) return null;
  return <ActiveStrip run={run} />;
}

function ActiveStrip({ run }: { run: AssistantRun }) {
  const { workspace } = useWorkspace();
  const elapsedMs = useElapsedMs(run.startedAt);

  const modelLabel = React.useMemo(() => {
    const match = workspace?.aiModels?.find((m) => m.id === run.model);
    return match?.label ?? run.model;
  }, [workspace?.aiModels, run.model]);

  const isWaiting = run.lifecycleState === 'awaiting_confirmation';

  const label = React.useMemo(() => {
    if (isWaiting) return 'Waiting for your input';
    const es = run.executionStatus;
    // A concrete, running step always wins over the generic lifecycle label.
    // lifecycleState only advances from 'pending_start' to 'streaming' when a
    // TEXT message starts, so any run that does real work without streaming
    // prose (card/tool/plan runs, the pre-token compute window) would otherwise
    // read "Preparing" for its whole duration even while a real step like
    // "Generate cost estimate" is running. Show the actual activity instead.
    // (This also keeps 'resuming' from masking a live step after the user
    // answers a prompt.)
    if (es?.activeStepId && isExecutionActive(es.phase)) {
      return getExecutionSummary(es);
    }
    // No concrete step running yet: lifecycle-specific waiting verbs. 'pending_start' reads
    // "Thinking" (not "Preparing") - a run that narrates via onProgress chain items (never streaming
    // a text message until its final summary) stays pending_start for its whole duration, so this is
    // the word shown during any compute gap between steps; "Thinking" reflects the model working,
    // where "Preparing" implied it had not started.
    if (run.lifecycleState === 'pending_start') return 'Thinking';
    if (run.lifecycleState === 'finalizing') return 'Finishing up';
    // 'resuming' = the run is continuing its OWN work after the user answered a prompt (e.g.
    // applying confirmed changes) - it is NOT a socket reconnect. Showing "Reconnecting" here made a
    // healthy multi-minute commit look like a dropped connection. Say "Working".
    if (run.lifecycleState === 'resuming') return 'Working';
    // Otherwise a phase verb (Thinking / Working / Responding).
    if (es && isExecutionActive(es.phase)) return getExecutionSummary(es);
    return 'Working';
  }, [isWaiting, run.lifecycleState, run.executionStatus]);

  return (
    <div
      className={`ai-chat-status-strip${isWaiting ? ' ai-chat-status-strip--waiting' : ''}`}
      role="status"
      aria-live="polite"
    >
      <span className="ai-chat-status-strip__dot" aria-hidden="true" />
      <span className="ai-chat-status-strip__label">{label}…</span>
      <span className="ai-chat-status-strip__elapsed">
        {/* whole seconds only - sub-second precision just flickers */}
        {formatDuration(Math.max(1, Math.round(elapsedMs / 1000)) * 1000)}
      </span>
      <span className="ai-chat-status-strip__model">{modelLabel}</span>
    </div>
  );
}
